import React from 'react';
import { H1, Button, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { useUser } from '../../../context/UserContext';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  OrderId,
  StatusContainer,
  Amount,
} from './styles';
import { getStatusIcon } from './statusIcon';

const MY_ORDERS_QUERY = gql`
  query MyOrders($customerId: Int, $vendorId: Int) {
    myOrders(customerId: $customerId, vendorId: $vendorId, limit: 100, offset: 0) {
      orderId
      status
      amount
      createdAt
      trackingNumber
      carrier
      counterpartyName
    }
  }
`;

interface MyOrder {
  orderId: string;
  status: string;
  amount: number;
  createdAt?: string | null;
  trackingNumber?: string | null;
  carrier?: string | null;
  counterpartyName?: string | null;
}

interface BuyerDashboardProps {
  onOrderClick?: (orderId: string) => void;
  onCreateOrder?: () => void;
  onBrowseMarketplace?: () => void;
}

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const numericValue = typeof value === 'number'
    ? value
    : Number(String(value).replace(/[^0-9.-]/g, ''));

  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const BuyerDashboard: React.FC<BuyerDashboardProps> = ({ onOrderClick, onBrowseMarketplace }) => {
  const { role } = useRole();
  const { user } = useUser();
  const isSupplier = role === 'supplier';
  const customerId = user?.customerId;
  const vendorId = user?.vendorId;
  const hasIdentity = customerId !== undefined || vendorId !== undefined;

  const { data, loading } = useGraphQLQuery<{ myOrders: MyOrder[] }>(MY_ORDERS_QUERY, {
    variables: { customerId: customerId ?? null, vendorId: vendorId ?? null },
    skip: !hasIdentity,
  });

  const orders = data?.myOrders ?? [];
  const distinctOrderIds = new Set(orders.map((o) => o.orderId));
  const activeOrderIds = new Set(orders.filter((o) => o.status !== 'Delivered').map((o) => o.orderId));
  const columnCount = isSupplier ? 6 : 5;

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>
            {isSupplier ? 'Your shipments, tracked end to end' : 'Your orders, tracked end to end'}
          </H1>
          <Subtitle>
            {isSupplier
              ? 'Production, customs, shipping and payment status for every order you’re fulfilling.'
              : 'Production, customs, shipping and payment status for every order you’re buying — however you found your exporter.'}
          </Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Total Orders</StatTitle>
              <StatValue>{loading ? '—' : distinctOrderIds.size}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Package" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Active Orders</StatTitle>
              <StatValue>{loading ? '—' : activeOrderIds.size}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Truck" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <OrdersHeader>
          <h3 style={{ margin: 0 }}>Your orders</h3>
          {!isSupplier && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="outline" onClick={onBrowseMarketplace}>Browse Marketplace</Button>
            </div>
          )}
        </OrdersHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Order ID</TableHeader>
                <TableHeader>{isSupplier ? 'Buyer' : 'Fulfilled by'}</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Tracking</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Amount</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {!hasIdentity && (
                <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TableCell colSpan={columnCount}>
                    <span style={{ color: colors.gray500 }}>
                      This login isn’t linked to a real {isSupplier ? 'vendor' : 'customer'} record yet, so there’s
                      no order history to show.
                    </span>
                  </TableCell>
                </TableRow>
              )}
              {hasIdentity && orders.map((row, index) => (
                <TableRow
                  key={`${row.orderId}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                >
                  <TableCell>
                    <OrderId style={{ cursor: 'pointer', color: colors.primary }} onClick={() => onOrderClick?.(row.orderId)}>
                      {row.orderId ?? '—'}
                    </OrderId>
                  </TableCell>
                  <TableCell>{row.counterpartyName ?? '—'}</TableCell>
                  <TableCell>
                    <StatusContainer>{getStatusIcon(row.status ?? 'Pending')}<span>{row.status ?? '—'}</span></StatusContainer>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: colors.gray500, fontSize: '0.75rem' }}>
                      {row.carrier ? `${row.carrier}${row.trackingNumber ? ` · ${row.trackingNumber}` : ''}` : '—'}
                    </span>
                  </TableCell>
                  <TableCell>{row.createdAt ?? '—'}</TableCell>
                  <TableCell><Amount>{formatAmount(row.amount)}</Amount></TableCell>
                </TableRow>
              ))}
              {hasIdentity && !loading && orders.length === 0 && (
                <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TableCell colSpan={columnCount}>
                    <span style={{ color: colors.gray500 }}>
                      {isSupplier
                        ? 'No orders yet — list a product on the Seller Panel to start receiving them.'
                        : 'No orders yet — browse the Marketplace, or use Create Order in the sidebar if you already have an exporter.'}
                    </span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </OrdersCard>
    </DashboardContainer>
  );
};
